import React, { useEffect, useState } from 'react';
import { Grid, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import { useForm } from '../components/useForm';
import Modal from '../components/Modal';
import EditSettingsForm from './EditSettingsForm';
import EditSettingsOptions from './EditSettingsOptions';
import ResetSettingButton from './ResetSettingButton';
import { Controls } from '../components/controls/Controls';
import AthenaSettingPane from './integrations/Athena/AthenaSettingPane';
import AccuroSettingPane from './integrations/Accuro/setting/AccuroSettingPane';
import TelusSettingPane from './integrations/Telus/setting/TelusSettingPane';
import {
  getCategorySettingOrder,
  sortSettingNamesByOrder,
  getSettingsForIntakeSubcategory,
  getSettingsForHxManagerSubcategory,
} from '../utils/categorySettingOrder';

const initialFieldValues = {
  name: '',
  category: '',
  value: '',
};

export default function SiteSettingsPane(props) {
  const {
    onSave, category, subCategory, currentSite, refreshParent, setErrorMessage, setSuccessMessage,
  } = props;
  const [mergedSettingsForCategory, setMergedSettingsForCategory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const {
    values, setValues, handleInputChange,
  } = useForm(initialFieldValues);

  const handleModalClose = () => {
    setIsEditing(false);
    setValues(initialFieldValues);
  };

  const handleSaveClick = () => {
    if (!values.name.trim() || !values.category.trim()) {
      setErrorMessage('Both name and category must be filled out.');
      return;
    }

    setIsEditing(false);
    onSave(values.name, values.category, values.value);
    setValues(initialFieldValues);
  };

  const getMergedSettingsForCategory = () => {
    if (!currentSite.settings) {
      return [];
    }

    const filterByCategory = (setting) => {
      if (setting.category !== category.id) return false;

      if (!subCategory) return true;

      // Special handling for intake subcategories
      if (category.id === 'intake') {
        const allowedSettings = getSettingsForIntakeSubcategory(subCategory.id);
        return allowedSettings.includes(setting.name);
      }

      if (category.id === 'hxManager') {
        const allowedSettings = getSettingsForHxManagerSubcategory(subCategory.id);
        return allowedSettings.includes(setting.name);
      }

      // Default behavior for other categories (like API)
      return setting.name.includes(subCategory.id);
    };

    const settings = currentSite.settings.filter(filterByCategory);
    const defaultSettings = currentSite.defaultSettings?.filter(filterByCategory) || [];

    // Get the fixed order for the current category
    const categorySettingOrder = getCategorySettingOrder(category.id);
    const positionMap = {};
    categorySettingOrder.forEach((name, index) => {
      positionMap[name] = index;
    });

    // Collect all unique setting names
    const allSettingNames = new Set();
    defaultSettings.forEach((setting) => allSettingNames.add(setting.name));
    settings.forEach((setting) => allSettingNames.add(setting.name));

    // Sort setting names by their predefined order
    const sortedNames = sortSettingNamesByOrder(Array.from(allSettingNames), positionMap);

    // Create merged settings maintaining the sorted order
    const mergedSettings = [];
    sortedNames.forEach((settingName) => {
      const customSetting = settings.find((s) => s.name === settingName);
      const defaultSetting = defaultSettings.find((s) => s.name === settingName);

      if (customSetting) {
        const isDefault = defaultSetting && defaultSetting.value === customSetting.value;
        mergedSettings.push({ ...customSetting, isDefault });
      } else if (defaultSetting) {
        mergedSettings.push({ ...defaultSetting, isDefault: true });
      }
    });

    return mergedSettings;
  };

  useEffect(() => {
    if (currentSite) {
      const mergedSettings = getMergedSettingsForCategory();
      setMergedSettingsForCategory(mergedSettings);
    }
  }, [category, currentSite, subCategory]);

  if (!currentSite) {
    return null;
  }
  const createDefaultApiSetting = (name) => ({ name, category: 'api', value: '' });
  const getSetting = (name) => mergedSettingsForCategory.find((setting) => setting.name === name)
    || createDefaultApiSetting(name);

  if (subCategory && subCategory.id === 'accuro') {
    const accuroSettings = {
      enabled: getSetting('accuroEnabled'),
      uuid: getSetting('accuroUUID'),
      accessToken: getSetting('accuroAccessToken'),
      refreshToken: getSetting('accuroRefreshToken'),
      accessTokenExpireTime: getSetting('accuroAccessTokenExpireTime'),
      refreshTokenExpireTime: getSetting('accuroRefreshTokenExpireTime'),
      filterByProviderIds: getSetting('accuroFilterByProviderIds'),
      providerIds: getSetting('accuroProviderIds'),
      appointmentReasonIds: getSetting('accuroAppointmentReasonIds'),
      appointmentType: getSetting('accuroAppointmentType'),
      sentSMSStatus: getSetting('accuroSentSMSStatus'),
      notSentSMSStatus: getSetting('accuroNotSentSMSStatus'),
      intakeCompletedStatus: getSetting('accuroIntakeCompletedStatus'),
      sendDays: getSetting('accuroSendDays'),
      daysBeforeAppointment: getSetting('accuroDaysBeforeAppointment'),
      preBookedAutomationEnabled: getSetting('accuroPreBookedAutomationEnabled'),
      sameDayAutomationEnabled: getSetting('accuroSameDayAutomationEnabled'),
      folderId: getSetting('accuroFolderId'),
      subFolderId: getSetting('accuroSubfolderId'),
      intakePlans: currentSite.settings.find((setting) => setting.name === 'intakePlans'),
    };
    return (
      <AccuroSettingPane
        entity={currentSite}
        accuroSettings={accuroSettings}
        onSave={onSave}
        setValues={setValues}
        refreshParent={refreshParent}
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
      />
    );
  }

  if (subCategory && subCategory.id === 'athena') {
    const athenaSettings = {
      enabled: mergedSettingsForCategory.find((setting) => setting.name === 'athenaEnabled')?.value === 'true',
      departmentId: mergedSettingsForCategory.find((setting) => setting.name === 'athenaDepartmentId')?.value,
      practiceId: mergedSettingsForCategory.find((setting) => setting.name === 'athenaPracticeId')?.value,
      providers: mergedSettingsForCategory.find((setting) => setting.name === 'athenaProviders')?.value,
      intakePlans: currentSite.settings.find((setting) => setting.name === 'intakePlans')?.value,
    };
    return (
      <AthenaSettingPane
        athenaSettings={athenaSettings}
        onSave={onSave}
      />
    );
  }

  if (subCategory && subCategory.id === 'telus') {
    const telusSettings = {
      enabled: getSetting('telusEnabled'),
      authCredentials: getSetting('telusAuthCredentials'),
      calendars: getSetting('telusCalendars'),
      providerIds: getSetting('telusProviderIds'),
      intakePlanId: getSetting('telusIntakePlanId'),
      appointmentCutoffDateTime: getSetting('telusAppointmentCutoffDateTime'),
      operatingHours: getSetting('telusOperatingHours'),
      timezone: getSetting('telusTimezone'),
      smsHoursBeforeAppointment: getSetting('telusSmsHoursBeforeAppointment'),
      appointmentTypes: getSetting('telusAppointmentTypes'),
      excludeNoTypeAppointments: getSetting('telusExcludeNoTypeAppointments'),
      intakePlans: currentSite.settings.find((setting) => setting.name === 'intakePlans'),
    };

    return (
      <TelusSettingPane
        entity={currentSite}
        telusSettings={telusSettings}
        onSave={onSave}
        setValues={setValues}
        refreshParent={refreshParent}
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
      />
    );
  }

  return (
    <div style={{ overflow: 'auto' }}>
      <div style={{ width: '100%' }}>
        {mergedSettingsForCategory.map((setting) => {
          const { isDefault, ...settingValues } = setting;

          return (
            <Grid container alignItems="center" spacing={2} key={setting.name} style={{ marginBottom: '30px', marginTop: '10px' }}>
              <Grid item>
                <Controls.Input
                  value={setting.name}
                  name="name"
                  label="Name"
                  variant="outlined"
                  disabled
                  width="370px"
                />
              </Grid>
              <Grid item>
                <EditSettingsOptions
                  setValues={setValues}
                  handleSaveClick={handleSaveClick}
                  handleEditClick={() => {
                    setIsEditing(true);
                    setValues(settingValues);
                  }}
                  values={settingValues}
                  refreshParent={refreshParent}
                  setSuccessMessage={setSuccessMessage}
                  setErrorMessage={setErrorMessage}
                  handleInputChange={handleInputChange}
                  contextType="site"
                />
              </Grid>
              <Grid item>
                {setting.isDefault
                  ? (
                    <Chip label="Default Setting" size="small" />
                  )
                  : (
                    <ResetSettingButton
                      entity={currentSite}
                      type="site"
                      setting={setting}
                      refreshParent={refreshParent}
                      setErrorMessage={setErrorMessage}
                      setSuccessMessage={setSuccessMessage}
                    />
                  )}

              </Grid>
            </Grid>
          );
        })}
      </div>
      <Modal isOpen={isEditing} handleClose={handleModalClose}>
        <Grid container>
          <Grid item xs={6}>
            <EditSettingsForm
              handleInputChange={handleInputChange}
              settingsForCategory={mergedSettingsForCategory}
              values={values}
              handleSaveClick={handleSaveClick}
            />
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
}

const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

SiteSettingsPane.propTypes = {
  onSave: PropTypes.func.isRequired,
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    subCategories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })),
  }).isRequired,
  currentSite: PropTypes.shape({
    settings: PropTypes.arrayOf(settingShape).isRequired,
    defaultSettings: PropTypes.arrayOf(settingShape).isRequired,
  }).isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  subCategory: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }),
};

SiteSettingsPane.defaultProps = {
  subCategory: null,
};
